/* eslint-disable no-console */
import * as R from '../../Router/routes';
import { applyAll, empty, notEmail } from '../../utils/validations';
import {
  formatBackendValidationErrors,
  isNowHoliday,
  ValidationError,
} from '../../utils';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { Helmet } from 'react-helmet';
import { memo, useCallback, useEffect, useState } from 'react';
import {
  useApplication,
  useAuth,
  useClient,
  useClientLoans,
  useLatestLoan,
} from '../../hooks/webapi';
import { useNavigate } from 'react-router-dom';
import { useSeon } from '../../hooks/useSeon';
import Button from '../../components/Button/Button';
import Flow from '../../components/Layouts/Flow/Flow';
import getClientEmail from '../../api/getClientEmail';
import getClientPhone from '../../api/getClientPhone';
import Heading from '../../components/Heading/Heading';
import HelpBlock from '../../components/HelpBlock/HelpBlock';
import IdentifyEvent from '../../hooks/context/EventCollector/events/IdentifyEvent';
import Input from '../../components/Input/Input';
import isCrossRedirectionNeeded from '../../utils/isCrossRedirectionNeeded';
import Link from '../../components/Link/Link';
import styles from './Login.module.scss';
import translate from '../../utils/translate';
import useEventCollector from '../../hooks/context/EventCollector/useEventCollector';

type LoginFormFields = { email: string; password: string };

const validate = (values: LoginFormFields) => {
  const errors: FormikErrors<LoginFormFields> = {};

  errors.email = applyAll(empty, notEmail)(values.email);

  errors.password = applyAll(empty)(values.password);

  const hasAnyError = Object.values(errors).filter(Boolean).length > 0;

  return hasAnyError ? errors : {};
};

const urlParams = new URLSearchParams(window.location.search);

const getClearQueryString = () => {
  urlParams.delete('sessionExpired');
  urlParams.delete('redirect');

  const paramsString = urlParams.toString();

  if (paramsString) {
    return decodeURIComponent(`?${paramsString}`);
  }

  return '';
};

export default memo(function Login() {
  const navigate = useNavigate();
  const { login, crossLoginRedirect } = useAuth();
  const [pending, setPending] = useState(false);
  const [serverError, setServerError] = useState<string>();
  const { fetchClient, postAnalyticsCookies } = useClient();
  const { fetchLatestLoan } = useLatestLoan();
  const { fetchClientLoans } = useClientLoans();
  const { fetchApplication } = useApplication();
  const { createSeonScript, sendSeonFraudInfo } = useSeon();
  const { identify } = useEventCollector();

  // eslint-disable-next-line react-hooks/exhaustive-deps -- we dont want infinite rerenders
  useEffect(() => createSeonScript(), []);

  const handleSubmit = useCallback(
    async (
      values: LoginFormFields,
      actions: FormikHelpers<LoginFormFields>,
    ) => {
      try {
        setPending(true);
        const response = await login(values.email, values.password);

        if (response?.accessVerificationRequired) {
          navigate(`${R.TWO_FA}`, {
            replace: true,
            state: {
              encodedCredentials: btoa(`${values.email}:${values.password}`),
            },
          });
          return;
        }

        postAnalyticsCookies();

        const clientLoans = await fetchClientLoans();
        if (clientLoans.clientLoanCount === 0) {
          await sendSeonFraudInfo();
        }

        const [client, latestLoan, emailData, phoneData, application] =
          await Promise.all([
            fetchClient(),
            fetchLatestLoan(),
            getClientEmail(),
            getClientPhone(),
            fetchApplication(),
          ]);

        identify(
          new IdentifyEvent({
            client_loan: latestLoan ?? undefined,
            client: client ?? undefined,
            email: emailData,
            phone: phoneData,
          }),
        );

        const crossRedirectionTo = isCrossRedirectionNeeded(client);

        if (crossRedirectionTo) {
          await crossLoginRedirect({ to: crossRedirectionTo });
          return;
        }

        const redirect = urlParams.get('redirect')
          ? decodeURIComponent(`${urlParams.get('redirect')}`)
          : undefined;

        if (response.fromTemporaryPassword) {
          navigate(R.PASSWORD_RECOVERY_NEW_PASSWORD);
        } else if (client && client.status === 'IDENTIFIED') {
          if (redirect) {
            navigate(`${redirect}${getClearQueryString()}`);
          } else if (latestLoan) {
            navigate(`${R.ACTIVE_LOAN}${getClearQueryString()}`);
          } else if (
            application?.resolution === 'REJECTED' &&
            application.resolutionDetail === 'UPSALE_OFFER'
          ) {
            navigate(`${R.NEW_OFFER_INCREASED}${getClearQueryString()}`);
          } else if (
            application?.resolution === 'REJECTED' &&
            application.resolutionDetail === 'NEW_OFFER'
          ) {
            navigate(`${R.NEW_OFFER_DECREASED}${getClearQueryString()}`);
          } else {
            navigate(`${R.REPEATED_LOAN}${getClearQueryString()}`);
          }
        } else {
          // TODO: redirect to default dashboard route, when dashboard will be done
          navigate(`${R.ACTIVE_LOAN}${getClearQueryString()}`);
        }
      } catch (e) {
        if (e instanceof ValidationError && e.fieldErrors.length) {
          const errors = formatBackendValidationErrors(e.fieldErrors);
          actions.setErrors(errors);
        } else if (e instanceof Error) {
          setServerError(e.message);
        }
        setPending(false);
      }
    },
    [
      login,
      postAnalyticsCookies,
      fetchClientLoans,
      fetchClient,
      fetchLatestLoan,
      fetchApplication,
      identify,
      navigate,
      sendSeonFraudInfo,
      crossLoginRedirect,
    ],
  );

  const formik = useFormik<LoginFormFields>({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: handleSubmit,
    validate,
  });

  useEffect(() => {
    // Remove server errors when user starts typing
    setServerError(undefined);
  }, [formik.values]);

  return (
    <Flow withMenu={false} className={styles.layout}>
      <Helmet>
        <title>{translate('login.html_title')}</title>
      </Helmet>
      <Heading size="l" className={styles.heading}>
        {translate('login.title')}
      </Heading>
      <form onSubmit={formik.handleSubmit}>
        <Input
          autoComplete="off"
          className={styles.input}
          name="email"
          placeholder={translate('login.email')}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          isTouched={formik.touched.email}
          error={formik.errors.email || Boolean(serverError)}
          dataTest="email-dni-input"
          stackable
        />
        <Input
          autoComplete="off"
          className={styles.input}
          name="password"
          type="password"
          placeholder={translate('login.password')}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.password}
          isTouched={formik.touched.password}
          error={formik.errors.password || serverError}
          showPasswordReveal={true}
          dataTest="password-input"
          eventCollector={{ dontTrackContent: true }}
        />
        <Button
          className={styles.submit}
          pending={pending}
          type="submit"
          dataTest="submit-button"
        >
          {translate('login.submit')}
        </Button>
      </form>
      <Link to={R.PASSWORD_RECOVERY} className={styles.link}>
        {translate('login.forgot_password')}
      </Link>
      <HelpBlock
        heading={translate('login.have_questions')}
        description={
          isNowHoliday()
            ? translate('login.contact_details_holiday')
            : translate('login.contact_details')
        }
      />
    </Flow>
  );
});
